// @flow

import { EXPLORER, SUMMARY } from '../constants/page-settings';

import * as UserModel from '../models/user';
import * as UserListModel from '../models/user-list';
import * as RecordingModel from '../models/recording';
import * as LocationModel from '../models/location';
import * as ReportModel from '../models/report';
import * as ActivesMetricsModel from '../models/active-metrics';
import * as QueryResponseListModel from '../models/query-response-list';
import * as AvailableMetricsModel from '../models/available-metrics';
import { ORG_HAS_STAFF_DETECTION } from '../constants/features';
import { getOptions } from '../models/user';
import * as PERIOD_PRESETS from '../constants/period-presets';
import * as PREVIOUS_PERIOD_PRESETS from '../constants/previous-period-presets';
import type { ReducerType as UserReducerT } from '../reducers/user';
import type { ReducerType as RecordingReducerT } from '../reducers/recordings';
import type { ReducerType as LocationReducerT } from '../reducers/locations';
import type { ReducerType as PageSettingsReducerT } from '../reducers/pageSettings';
import type { ReducerType as AsyncProcessesReducerT } from '../reducers/asyncProcesses';
import type { ReducerType as ReportReducerT } from '../reducers/reports';
import type {
  ReducerType as UsersReducerT,
  UserStatusKey,
  EmbeddedIncludeKey,
} from '../reducers/users';
import type { ReducerType as EntityTagsReducerT } from '../reducers/entityTags';
import type { ChatThread } from '../reducers/chatThreads';
import type { CombinedReducersT } from '../reducers';

export const userHasFeature = (
  featureName: string,
  {
    user,
  }: {
    user: UserReducerT,
  },
): boolean => {
  if (!user || !user.model) return false;
  const { features } = user.model;
  if (!features) return false;

  const result = features[featureName];

  if (!result) return false;
  if (result === 'on') return true;
  if (result === 'off') return false;
  return false;
};

export const valueOfUserFeature = (
  featureName: string,
  {
    user,
  }: {
    user: UserReducerT,
  },
): string => {
  if (!user || !user.model) return '';
  const { features } = user.model;
  if (!features) return '';
  const result = features[featureName];
  if (!result) return '';
  return result;
};

export const userHasPermission = (
  permissionName: string,
  {
    user,
  }: {
    user: UserReducerT,
  },
): boolean => {
  if (!user || !user.model) return false;
  const { permissions } = user.model;
  if (!permissions) return false;
  return permissions[permissionName];
};

export const getUserModel = ({
  user,
}: {
  user: UserReducerT,
}): ?UserModel.t => {
  if (!!user && !!user.model) return user.model;
  return null;
};

export const getLocations = ({
  locations,
}: {
  locations: LocationReducerT,
}): LocationModel.t[] => {
  return (
    locations.sort((a, b) => {
      if (a.name && b.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    }) || []
  );
};

export const getOrganisationName = ({
  user,
}: {
  user: UserReducerT, // TODO: this uses the reducer type, but shouldn't it use the model type?
}): string => {
  if (!!user && !!user.model) return UserModel.getOrganisationName(user.model);
  return '';
};

export const getOrganisationId = ({
  user,
}: {
  user: UserReducerT, // TODO: this uses the reducer type, but shouldn't it use the model type?
}): string => {
  if (!!user && !!user.model) return UserModel.getOrganisationId(user.model);
  return '';
};

export const userRefreshCompleted = ({
  asyncProcesses,
}: {
  asyncProcesses: AsyncProcessesReducerT,
}): boolean => asyncProcesses.userRefresh.completed;

export const userRefreshAuthFailure = ({
  asyncProcesses,
}: {
  asyncProcesses: AsyncProcessesReducerT,
}): boolean =>
  userRefreshCompleted({ asyncProcesses }) &&
  !!asyncProcesses.userRefresh.responseFailureCode &&
  asyncProcesses.userRefresh.responseFailureCode >= 400 &&
  asyncProcesses.userRefresh.responseFailureCode < 500;

export const asyncProcessHappening = (
  { asyncProcesses }: { asyncProcesses: AsyncProcessesReducerT }, // todo: should use AsyncProcessesReducerT here but it doesnt work
  key: string,
): boolean =>
  asyncProcesses.ongoingProcesses.some((activeProcess) =>
    typeof activeProcess === 'string'
      ? activeProcess.startsWith(key)
      : activeProcess.process.startsWith(key),
  );

export const getRecordings = ({
  recordings,
}: {
  recordings: RecordingReducerT,
}): RecordingReducerT => recordings;

export const getEntityTags = (state: any) => {
  const et = state && state.entityTags;
  if (!et || !et.allIds) return [];
  return et.allIds.map((id) => et.byId[id]).filter(Boolean);
};

// Raw entityTags reducer slice. Use when callers need byId/allIds lookups
// (e.g. registry-driven preference resolution); use `getEntityTags` when
// you just want a flat list of all tags.
export const getEntityTagsState = (state: any): EntityTagsReducerT => {
  const et = state && state.entityTags;
  if (et && typeof et === 'object') return et;
  return { byId: {}, allIds: [], loading: false, error: null };
};

export const getEntityTagsLoading = (state: any): boolean =>
  !!(state && state.entityTags && state.entityTags.loading);

export const getEntityTagById = (state: any, id: string) => {
  const et = state && state.entityTags;
  if (!et || !et.byId) return null;
  return et.byId[id] || null;
};

export const getTagsForLocation = (state: any, locationId: string) => {
  const loc = (state.locations || []).find((l) => l.id === locationId);
  if (!loc || !loc.tags) return [];
  const byId = (state.entityTags && state.entityTags.byId) || {};
  return loc.tags.map((id) => byId[id]).filter(Boolean);
};

export const getReports = ({
  reports,
  user,
}: {
  reports: ReportReducerT,
  user: UserReducerT,
}): ReportReducerT => {
  let showDraftReports = false;
  if (user && user.model && getOptions(user.model)) {
    showDraftReports = getOptions(user.model).showDraftReports;
  }

  return reports.filter(
    (r) =>
      (showDraftReports && r.status === 'draft') ||
      r.status === 'active' ||
      !r.status,
  );
};

// Shared sort helper for reports: order desc, then createdAt desc
const sortReportsDesc = (a, b) => {
  const aOrder = typeof a.order === 'number' ? a.order : Number.NEGATIVE_INFINITY;
  const bOrder = typeof b.order === 'number' ? b.order : Number.NEGATIVE_INFINITY;
  if (aOrder !== bOrder) return bOrder - aOrder; // desc
  const aCreated = a && a.createdAt ? new Date(a.createdAt).getTime() : 0;
  const bCreated = b && b.createdAt ? new Date(b.createdAt).getTime() : 0;
  return bCreated - aCreated; // desc
};

export const getOrgReports = (state) => {
  const all = getReports(state) || [];
  return all
    .filter((r) => !r.ownerId)
    .sort(sortReportsDesc);
};

export const getMyReports = (state) => {
  const all = getReports(state) || [];
  // Backend will only deliver user-owned reports with ownerId set
  return all
    .filter((r) => r && r.ownerId)
    .sort(sortReportsDesc);
};

// Pinned threads first (most recently pinned first), then unpinned by updatedAt desc.
// pinnedDate is Unix SECONDS (see aura-api pinning/rename doc §3) — numeric compare.
const toTime = (iso) => {
  const t = iso ? new Date(iso).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

export const getSortedChatThreads = (
  state: CombinedReducersT,
): ChatThread[] => {
  const threads: ChatThread[] =
    (state.chatThreads && state.chatThreads.threads) || [];
  return [...threads].sort((a, b) => {
    const aPinned = !!(a && a.pinned);
    const bPinned = !!(b && b.pinned);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    if (aPinned && bPinned) {
      return (b.pinnedDate || 0) - (a.pinnedDate || 0);
    }
    return toTime(b && b.updatedAt) - toTime(a && a.updatedAt);
  });
};

export const getOneReport = (
  id: string,
  state: CombinedReducersT,
): ?ReportModel.t => {
  const reports = getReports(state);
  if (!reports) return undefined;
  return ReportModel.findById(reports, id);
};

export const getCampaigns = ({
  campaigns,
}: {
  campaigns: CampaignReducerT,
}): CampaignReducerT => {
  if (!campaigns || !campaigns.length) {
    return [
      {
        id: 'dummy-1',
        name: 'Store Entry Test Campaign',
        ownerId: 'owner-1',
        ownerName: 'John Doe',
        created: new Date('2025-04-01'),
        metric: {
          key: 'entries',
          display: 'Store > Entries'
        },
        testLocations: ['location-1', 'location-2'],
        testPeriod: {
          selectedPreset: PERIOD_PRESETS.custom,
          selectedDates: {  
            start: '2025-04-01',
            end: '2025-04-30'
          },
          customPeriodDates: {  
            start: '2025-04-01',
            end: '2025-04-30'
          },
          active: true
        },
        controlPeriod: {  
          selectedPreset: PREVIOUS_PERIOD_PRESETS.custom,
          selectedDates: {
            start: '2025-03-01',
            end: '2025-03-31'
          },
          customPeriodDates: {
            start: '2025-03-01',
            end: '2025-03-31'
          },
          active: true
        },
        selectedBreakdowns: [
          {
            key: 'ages',
            values: ['0_15', '16_24']
          }
        ]
      },
      {
        id: 'dummy-2',
        name: 'Heatmap Analysis Campaign',
        ownerId: 'owner-2',
        ownerName: 'Jane Smith',
        created: new Date('2025-04-02'),
        metric: {
          key: 'heatmap',
          display: 'Store > Heatmap'
        },
        testLocations: ['location-3'],
        testPeriod: {
          selectedPreset: PERIOD_PRESETS.custom,
          selectedDates: {
            start: '2025-04-01',
            end: '2025-04-15'
          },
          customPeriodDates: {
            start: '2025-04-01',
            end: '2025-04-15'
          },
          active: true
        },
        controlPeriod: {
          selectedPreset: PREVIOUS_PERIOD_PRESETS.custom,
          selectedDates: {
            start: '2025-03-01',
            end: '2025-03-15'
          },
          customPeriodDates: {
            start: '2025-03-01',
            end: '2025-03-15'
          },
          active: true
        },
        selectedBreakdowns: [
          {
            key: 'ages',
            values: ['25_34', '35_44']
          },
          {
            key: 'gender',
            values: ['male', 'female']
          }
        ]
      }
    ];
  }
  return campaigns;
};

// Add to selectors/index.js
export const getOneCampaign = (id: string, state: CombinedReducersT): ?CampaignModel.t => {
  const campaigns = getCampaigns(state);
  if (!campaigns) return undefined;
  return campaigns.find(campaign => campaign.id === id);
};

export const getOneRecording = (
  {
    recordings,
  }: {
    recordings: RecordingReducerT,
  },
  id: ?string,
): ?RecordingModel.t => {
  // TODO: use RecordingModel.findById (which in turn uses Arr.find)
  const filtered = recordings.filter((r) => r.id === id);
  return filtered.length === 1 ? filtered[0] : null;
};

export const getPageSettings = (
  { pageSettings }: { pageSettings: PageSettingsReducerT },
  pageName: string,
) => pageSettings[pageName];

export const getQueryResponseList = ({
  queryResponseList,
}: {
  queryResponseList: QueryResponseListModel.t,
}) => queryResponseList;

export const getAvailableMetrics = ({
  user,
}: {
  user: UserReducerT,
}): ?AvailableMetricsModel.t => {
  const model = user && getUserModel({ user });
  return model && UserModel.getAvailableMetrics(model);
};

export const getAvailableMetricGroups = (state: {
  user: UserReducerT,
}): string[] => {
  const metrics = getAvailableMetrics(state);
  return metrics ? Object.keys(metrics) : [];
};

export const isDemographicFilterRoleCustomer = (state?: Object): boolean => {
  if (!state || !state.demographicFilter || !state.demographicFilter.role) {
    return false;
  }
  return state.demographicFilter.role === 'customer';
};

export const sanitisePayloadDemographics = (state: Object): {
    ages?: string[],
    genders?: string[],
    roles: string[],
} => {
    return {
        ages: state.ages
            ? state.ages
            : state.demographicFilter?.age
                ? [state.demographicFilter.age]
                : undefined,
        genders: state.genders
            ? state.genders
            : state.demographicFilter?.gender
                ? [state.demographicFilter.gender]
                : undefined,
        roles: state.roles
            ? state.roles
            : state.demographicFilter?.role
                ? [state.demographicFilter.role]
                : ["customer", "staff"],
    }
}

export const isUserNewlyCreated = (state: CombinedReducersT): boolean => {
  const userModel = getUserModel(state);
  if (!userModel) return false;
  const status = UserModel.getUserStatus(userModel);
  return status === 'created';
};

export const getPageQueryResponseList = (
  state: CombinedReducersT,
  queryResponseList: QueryResponseListModel.t,
  pageSettingsType?: 'explorer' | 'summary',
) => {
  if (
    !pageSettingsType ||
    (pageSettingsType !== EXPLORER && pageSettingsType !== SUMMARY)
  ) {
    return queryResponseList;
  }

  const settings = getPageSettings(state, pageSettingsType);

  const { metrics, period, aggregation, activeMetrics } = settings;
  let metricsToUse = [];
  let aggregationToUse = pageSettingsType === EXPLORER ? aggregation : 'day';

  if (pageSettingsType === EXPLORER) {
    metricsToUse = metrics;
  } else if (pageSettingsType === SUMMARY) {
    metricsToUse =
      ActivesMetricsModel.getColumnsFromActiveMetrics(activeMetrics);
  }

  let filteredComparisonQueryResponseList = [];
  const filteredQueryResponseList = metricsToUse
    ? metricsToUse.reduce((finalFilteredQueryResponseList, metric) => {
        const correctMetric =
          pageSettingsType === EXPLORER && metric.metric
            ? metric.metric
            : metric;
        const correctPeriod = period.selectedDates;

        const excludeStaff =
          pageSettingsType === EXPLORER
            ? isDemographicFilterRoleCustomer(metric) ||
              !userHasFeature(ORG_HAS_STAFF_DETECTION, state)
            : isDemographicFilterRoleCustomer(settings) ||
              !userHasFeature(ORG_HAS_STAFF_DETECTION, state);

        if (pageSettingsType === SUMMARY) {
          /*
            This block is being called when:
            - In SUMMARY page we get each single location (as string)
          */
          const findCriteria = {
            ...correctMetric,
            excludeStaff,
            isComparisonActive: false,
            aggregation: aggregationToUse,
            period: correctPeriod,
            isAllLocations: false,
            locations: settings.filteredLocations,
            breakdownByDimensions: ['entity'],
            taxonomies: correctMetric.taxonomies
              ? correctMetric.taxonomies
              : undefined,
          };

          if (findCriteria.filter) {
            findCriteria.taxonomies = [
              `${findCriteria.filter.key}:${findCriteria.filter.value}`,
            ];
            delete findCriteria.filter;
          }
          const queryResponse = QueryResponseListModel.findAll(
            findCriteria,
            queryResponseList,
          );

          if (queryResponse) {
            finalFilteredQueryResponseList.push(...queryResponse);
          }
        }

        /*
          This block is being called when:
            - In SUMMARY page we get the allLocations queryResponses (as an array)
            - In EXPLORER page we get all the locations we need in location (as an array)
        */
        const findCriteria = {
          ...correctMetric,
          excludeStaff,
          isComparisonActive: false,
          aggregation: aggregationToUse,
          period: correctPeriod,
          isAllLocations: false,
          locations:
            pageSettingsType === EXPLORER
              ? // $FlowFixMe -> Flow doesn't know what metric is
                metric.location
              : settings.filteredLocations,
          breakdownByDimensions: undefined,
          taxonomies: correctMetric.taxonomies
            ? correctMetric.taxonomies
            : undefined,
        };

        if (findCriteria.filter) {
          findCriteria.taxonomies = [
            `${findCriteria.filter.key}:${findCriteria.filter.value}`,
          ];
          delete findCriteria.filter;
        }

        if (pageSettingsType === SUMMARY) {
          findCriteria.isAllLocations = true;
        }

        const queryResponse = QueryResponseListModel.findAll(
          findCriteria,
          queryResponseList,
          {
            ignoreBreakdownBy: true,
          },
        );

        if (queryResponse) {
          finalFilteredQueryResponseList.push(...queryResponse);
        }

        return finalFilteredQueryResponseList;
      }, [])
    : [];

  /*
    This block is being called when:
      - In SUMMARY page you have isComparisonActive.
      - In EXPLORER page you have compareToPast.active
  */
  if (settings.compareToPast.active && settings.compareToPast.selectedDates) {
    filteredComparisonQueryResponseList = metricsToUse
      ? metricsToUse.reduce((finalFilteredQueryResponseList, metric) => {
          const correctMetric =
            pageSettingsType === EXPLORER && metric.metric
              ? metric.metric
              : metric;
          const correctPeriod = settings.compareToPast.selectedDates;

          const excludeStaff =
            pageSettingsType === EXPLORER
              ? isDemographicFilterRoleCustomer(metric) ||
                !userHasFeature(ORG_HAS_STAFF_DETECTION, state)
              : isDemographicFilterRoleCustomer(settings) ||
                !userHasFeature(ORG_HAS_STAFF_DETECTION, state);

          if (pageSettingsType === SUMMARY) {
            /*
              This block is being called when:
                - In SUMMARY page we get each single location (as string).
            */
            const findCriteria = {
              ...correctMetric,
              excludeStaff,
              isComparisonActive: true,
              isAllLocations: false,
              aggregation: aggregationToUse,
              period: correctPeriod,
              locations: settings.filteredLocations,
              breakdownByDimensions: ['entity'],
              taxonomies: correctMetric.taxonomies
                ? correctMetric.taxonomies
                : undefined,
            };

            if (findCriteria.filter) {
              findCriteria.taxonomies = [
                `${findCriteria.filter.key}:${findCriteria.filter.value}`,
              ];
              delete findCriteria.filter;
            }

            const queryResponse = QueryResponseListModel.findAll(
              findCriteria,
              queryResponseList,
            );

            if (queryResponse) {
              finalFilteredQueryResponseList.push(...queryResponse);
            }
          }

          /*
            This block is being called when:
              - In SUMMARY page we get the allLocations queryResponses (as an array).
              - In EXPLORER page we get all the locations we need in location.include (as an array).
          */
          const findCriteria = {
            ...correctMetric,
            excludeStaff,
            aggregation: aggregationToUse,
            period: correctPeriod,
            isComparisonActive: true,
            isAllLocations: false,
            locations:
              pageSettingsType === EXPLORER
                ? // $FlowFixMe -> Flow doesn't know what metric is
                  metric.location
                : settings.filteredLocations,
            breakdownByDimensions: undefined,
            taxonomies: correctMetric.taxonomies
              ? correctMetric.taxonomies
              : undefined,
          };

          if (findCriteria.filter) {
            findCriteria.taxonomies = [
              `${findCriteria.filter.key}:${findCriteria.filter.value}`,
            ];
            delete findCriteria.filter;
          }

          if (pageSettingsType === SUMMARY) {
            findCriteria.isAllLocations = true;
          }

          const queryResponse = QueryResponseListModel.findAll(
            findCriteria,
            queryResponseList,
            {
              ignoreBreakdownBy: true,
            },
          );

          if (queryResponse) {
            finalFilteredQueryResponseList.push(...queryResponse);
          }

          return finalFilteredQueryResponseList;
        }, [])
      : [];
  }
  return [...filteredComparisonQueryResponseList, ...filteredQueryResponseList];
};

export const getLocationById = (
  state: CombinedReducersT,
  id: string,
): ?LocationModel.t => {
  const locations = getLocations(state);

  if (locations && locations.length > 0) {
    const location = locations.find((loc) => loc.id === id);

    return location || null;
  }

  return null;
};

// ── Users slice selectors ────────────────────────────────────────

const sortUsersByEmail = (
  a: UserListModel.userT,
  b: UserListModel.userT,
): number => (a.email > b.email ? 1 : -1);

const materialiseStatus = (
  state: { users: UsersReducerT },
  status: UserStatusKey,
): UserListModel.userT[] => {
  const slice = state && state.users;
  if (!slice) return [];
  const group = slice.byStatus && slice.byStatus[status];
  if (!group) return [];
  return group.ids
    .map((id) => slice.byId[id])
    .filter(Boolean);
};

export const getAllUsers = (state: {
  users: UsersReducerT,
}): UserListModel.userT[] => {
  const slice = state && state.users;
  if (!slice) return [];
  return [
    ...materialiseStatus(state, 'normal'),
    ...materialiseStatus(state, 'invited'),
    ...materialiseStatus(state, 'adopted'),
  ];
};

export const getUsersByStatus = (
  state: { users: UsersReducerT },
  status: UserStatusKey,
): UserListModel.userT[] => materialiseStatus(state, status);

export const getActiveTableUsers = (state: {
  users: UsersReducerT,
}): UserListModel.userT[] => {
  const normal = materialiseStatus(state, 'normal');
  const adopted = materialiseStatus(state, 'adopted');
  return [...normal, ...adopted].sort(sortUsersByEmail);
};

export const getUserById = (
  state: { users: UsersReducerT },
  userId: number,
): ?UserListModel.userT => {
  const slice = state && state.users;
  if (!slice) return null;
  return slice.byId[userId] || null;
};

export const getPreferencesForUser = (
  state: { users: UsersReducerT },
  userId: number,
): Object[] => {
  const u = getUserById(state, userId);
  if (!u || !Array.isArray(u.preferences)) return [];
  return u.preferences;
};

// Reads the role rows embedded on a user by `?include=roles` out of the users
// reducer. Returns an empty array when the user is unknown or its list was
// fetched without the roles include.
export const getRolesForUser = (
  state: { users: UsersReducerT },
  userId: number,
): UserListModel.roleDataT[] => {
  const u = getUserById(state, userId);
  if (!u || !Array.isArray(u.roles)) return [];
  return u.roles;
};

export const getUsersLoading = (
  state: { users: UsersReducerT },
  status: UserStatusKey,
): boolean => {
  const slice = state && state.users;
  if (!slice) return false;
  const group = slice.byStatus && slice.byStatus[status];
  return !!(group && group.loading);
};

export const getUsersError = (
  state: { users: UsersReducerT },
  status: UserStatusKey,
): ?string => {
  const slice = state && state.users;
  if (!slice) return null;
  const group = slice.byStatus && slice.byStatus[status];
  return (group && group.error) || null;
};

export const getEmbeddedIncludes = (
  state: { users: UsersReducerT },
  status: UserStatusKey,
): ?$ReadOnlyArray<EmbeddedIncludeKey> => {
  const slice = state && state.users;
  if (!slice) return null;
  const group = slice.byStatus && slice.byStatus[status];
  return group ? group.embeddedIncludes : null;
};
